(ns kyber.events.media-player
  (:require [cljs-time.core :as t]
            [re-frame.core :as re-frame]

            [kyber.utils :refer [map-user-notes-to-moments]]
            [kyber.constants :refer [html5-media-player-events
                                     active-moment-threshold]]
            [kyber.db.media-player :refer [db->play-on-initialize]]
            [kyber.events.utils :refer [kyber-event-db kyber-event-fx]
             :as kyber-event-utils]
            [applied-science.js-interop :as j]))

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Helpers
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(defn- player-assoc-in
  [db player-key attribute value]
  (assoc-in db [:media-player player-key :attributes attribute] value))

(def video-event-context {:player-key :string
                          :video-time :number
                          :src :string})

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Set duration
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-set-duration
    {:description
     (str "Attempts to set the duration of the player object if availible. If "
          "not availible, it will try to set again in 200ms.")
     :changelog
     [{:date "17DEC18"
       :event-version 0}]}))

(defn set-duration
  [{:keys [db]} [event-name {:keys [player player-key context] :as props}]]
  (let [duration (.duration player)
        is-set (not (js/Number.isNaN duration))]
    (cond-> {:db db
             :dispatch-n [[:analytics/save-datapoint
                           (merge
                            context
                            {:event event-name
                             :event-version 0})]]}
      (true? is-set)
      (assoc-in [:db :media-player player-key :attributes :duration] duration)

      (false? is-set)
      (update :dispatch-n conj [:dispatch-in 200 event-name props]))))

(kyber-event-fx :media-player/set-duration set-duration)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Initialize a Kyber Video Player Instance
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-initialize
    {:description
     "A new player has been initialized"
     :data-warehouse true
     :changelog
     [{:date "13MAY18"
       :event-version 0
       :data video-event-context}]}))

(defn initialize
  "Adds a video player instance to global state"
  [{:keys [db]} [event-name {:keys [player player-key context options]}]]
  (cond->
   {:db (assoc-in db
                  [:media-player player-key]
                  {:player player
                   :attributes {:show-volume-controls false
                                :player-state :pause
                                :loaded-metadata false
                                :suspended false
                                :volume (.volume player)
                                :rate (.playbackRate player)
                                :time (.currentTime player)
                                :duration 0}
                   :options options})
    :dispatch-n [[:analytics/save-datapoint
                  (merge
                   context
                   {:event event-name
                    :event-version 0})]]}

    (true? (db->play-on-initialize db player-key))
    (assoc :media-player/play player)))

(kyber-event-fx :media-player/initialize initialize)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Teardown a Kyber Video Player Instance
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-teardown
    {:description
     "A player is tearing down"
     :changelog
     [{:date "20SEP18"
       :event-version 0
       :data {:player-key :string}}]}))

(defn teardown
  [{:keys [db]} [event-name player-key]]
  {:db (update db :media-player #(dissoc % player-key))
   :media-player/teardown (get-in db [:media-player player-key :player])
   :dispatch [:analytics/save-datapoint
              {:event event-name
               :event-version 0
               :player-key player-key}]})

(kyber-event-fx :media-player/teardown teardown)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Rewind Media Element 15 Seconds
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-rewind
    {:description
     "User has interacted with the rewind button"
     :changelog
     [{:date "05JUL18"
       :event-version 0
       :data {:rewind-seconds :number
              :player-key :string}}]}))

(defn rewind
  [{:keys [db]} [event-name player-key]]
  (let [rewind-seconds 15
        player (get-in db [:media-player player-key :player])]
    (cond-> {:db db}
      (some? player)
      (merge {:dispatch-n [[:analytics/save-datapoint
                            {:event event-name
                             :event-version 0
                             :rewind-seconds rewind-seconds
                             :player-key player-key}]]
              :media-player/seek {:player player
                                  :position (js/Math.max
                                             (- (.currentTime player)
                                                rewind-seconds) 0)}}))))

(kyber-event-fx :media-player/rewind rewind)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Seeks to a position in media
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-seek
    {:description
     "User has seeked to another point in the media"
     :changelog
     [{:date "06JUL18"
       :event-version 0
       :data {:position :number
              :player-key :string}}]}))

(defn seek
  [{:keys [db]} [event-name {:keys [position player-key]}]]
  (let [player (get-in db [:media-player player-key :player])]
    (cond-> {:db db}
      (some? player)
      (merge {:dispatch-n [[:analytics/save-datapoint
                            {:event event-name
                             :event-version 0
                             :position position
                             :player-key player-key}]]
              :media-player/seek {:player player
                                  :position position}}))))

(kyber-event-fx :media-player/seek seek)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Show/hide volume controls
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-toggle-volume-controls
    {:description
     "Volume control visibility has been toggled"
     :changelog
     [{:date "06JUL18"
       :event-version 0
       :data {:controls-show :boolean}}]}))

(defn toggle-volume-controls
  [{:keys [db]} [event-name {:keys [player-key action]}]]
  (let [toggle-value
        (= action :open)
        current-value
        (get-in db
                [:media-player player-key :attributes :show-volume-controls])]
    (if (some? current-value)
      {:db (assoc-in db
                     [:media-player
                      player-key
                      :attributes
                      :show-volume-controls]
                     toggle-value)
       :dispatch-n [[:analytics/save-datapoint
                     {:event event-name
                      :event-version 0
                      :controls-shown toggle-value}]]}
      {:db db})))

(kyber-event-fx :media-player/toggle-volume-controls toggle-volume-controls)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Adjusts volume
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-adjust-volume
    {:description
     "Playback volume has been requested to change"
     :changelog
     [{:date "06JUL18"
       :event-version 0
       :data {:player-key :string
              :volume :number}}]}))

(defn adjust-volume
  [{:keys [db]} [event-name {:keys [volume player-key]}]]
  (let [player (get-in db [:media-player player-key :player])]
    (cond-> {:db db}
      (some? player)
      (merge {:dispatch-n [[:analytics/save-datapoint
                            {:event event-name
                             :event-version 0
                             :volume volume
                             :player-key player-key}]]
              :media-player/volume {:player player
                                    :volume volume}}))))

(kyber-event-fx :media-player/adjust-volume adjust-volume)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Adjusts Media Playback Rate
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-adjust-playback-rate
    {:description
     "Playback rate has been requested to change"
     :changelog
     [{:date "06JUL18"
       :event-version 0
       :data {:rate :number
              :player-key :string}}]}))

(defn adjust-playback-rate
  [{:keys [db]} [event-name {:keys [rate player-key]}]]
  (let [player (get-in db [:media-player player-key :player])]
    (cond-> {:db db}
      (some? player)
      (merge {:dispatch-n [[:analytics/save-datapoint
                            {:event event-name
                             :event-version 0
                             :rate rate
                             :player-key player-key}]]
              :media-player/playback-rate {:player player
                                           :rate rate}}))))

(kyber-event-fx :media-player/adjust-playback-rate adjust-playback-rate)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Plays Media Element
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-play-media
    {:description
     "User initiated play event"
     :changelog
     [{:date "08JAN19"
       :event-version 0
       :data {:player-key :string}}]}))

(defn play-media
  [{:keys [db]} [event-name {:keys [player-key]}]]
  (let [player (get-in db [:media-player player-key :player])]
    (cond-> {:db db}
      (some? player)
      (merge {:dispatch-n [[:analytics/save-datapoint
                            {:event event-name
                             :event-version 0
                             :player-key player-key}]]
              :media-player/play player}))))

(kyber-event-fx :media-player/play-media play-media)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Pauses Media Element
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-pause-media
    {:description
     "User initiated pause event"
     :changelog
     [{:date "08JAN19"
       :event-version 0
       :data {:player-key :string}}]}))

(defn pause-media
  [{:keys [db]} [event-name {:keys [player-key]}]]
  (let [player (get-in db [:media-player player-key :player])
        show-volume
        (get-in db
                [:media-player player-key :attributes :show-volume-controls])]
    (cond-> {:db db}
      (and (some? player)
           (false? show-volume))
      (merge {:dispatch-n [[:analytics/save-datapoint
                            {:event event-name
                             :event-version 0
                             :player-key player-key}]]
              :media-player/pause player}))))

(kyber-event-fx :media-player/pause-media pause-media)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; HTML5 Video Player Events
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Abort
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-abort
    {:description
     (str "HTML5 Video 'abort' event. \"The user agent stops fetching the "
          "media data before it is completely downloaded, but not due to an "
          "error.\"")
     :changelog
     [{:date "13MAY18"
       :event-version 0
       :data video-event-context}]}))

(kyber-event-fx
 :media-player/abort
 (fn [{:keys [db]} [event-name {:keys [player-key context]}]]
   (let [player (get-in db [:media-player player-key :player])]
     (cond-> {:db db}
       (some? player)
       (assoc :dispatch [:analytics/save-datapoint
                         (merge
                          context
                          {:event event-name
                           :event-version 0
                           :video-time (.currentTime player)})])))))

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Can Play
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-canplay
    {:description
     (str "HTML5 Video 'canplay' event. \"The user agent can resume playback "
          "of the media data, but estimates that if playback were to be "
          "started now, the media resource could not be rendered at the "
          "current playback rate up to its end without having to stop for "
          "further buffering of content.\"")
     :changelog
     [{:date "13MAY18"
       :event-version 0
       :data video-event-context}]}))

(kyber-event-fx
 :media-player/canplay
 (fn [{:keys [db]} [event-name {:keys [player-key context]}]]
   (let [player (get-in db [:media-player player-key :player])]
     (cond-> {:db db}
       (some? player)
       (assoc :dispatch [:analytics/save-datapoint
                         (merge
                          context
                          {:event event-name
                           :event-version 0
                           :video-time (.currentTime player)})])))))

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Can Play Through
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-canplaythrough
    {:description
     (str "HTML5 Video 'canplaythrough' event. \"The user agent estimates that "
          "if playback were to be started now, the media resource could be "
          "rendered at the current playback rate all the way to its end "
          "without having to stop for further buffering.\"")
     :data-warehouse true
     :changelog
     [{:date "13MAY18"
       :event-version 0
       :data video-event-context}]}))

(kyber-event-fx
 :media-player/canplaythrough
 (fn [{:keys [db]} [event-name {:keys [player-key context]}]]
   (let [player (get-in db [:media-player player-key :player])]
     (cond-> {:db db}
       (some? player)
       (assoc :dispatch [:analytics/save-datapoint
                         (merge
                          context
                          {:event event-name
                           :event-version 0
                           :video-time (.currentTime player)})])))))

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Duration Change
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-durationchange
    {:description
     (str "HTML5 Video 'durationchange' event. \"The duration attribute has "
          "just been updated.\"")
     :data-warehouse true
     :changelog
     [{:date "13MAY18"
       :event-version 0
       :data (merge video-event-context
                    {:duration :number})}]}))

(kyber-event-fx
 :media-player/durationchange
 (fn [{:keys [db]} [event-name {:keys [player-key context]}]]
   (let [player (get-in db [:media-player player-key :player])]
     (cond-> {:db db}
       (some? player)
       (assoc :dispatch [:analytics/save-datapoint
                         (merge
                          context
                          {:event event-name
                           :event-version 0
                           :video-time (.currentTime player)
                           :duration (.duration player)})])))))

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Emptied
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-emptied
    {:description
     (str "HTML5 Video 'emptied' event. \"A media element whose networkState "
          "was previously not in the NETWORK_EMPTY state has just switched to "
          "that state (either because of a fatal error during load that’s "
          "about to be reported, or because the load() method was invoked "
          "while the resource selection algorithm was already running).\"")
     :data-warehouse true
     :changelog
     [{:date "13MAY18"
       :event-version 0
       :data video-event-context}]}))

(kyber-event-fx
 :media-player/emptied
 (fn [{:keys [db]} [event-name {:keys [player-key context]}]]
   (let [player (get-in db [:media-player player-key :player])]
     (cond-> {:db db}
       (some? player)
       (assoc :dispatch [:analytics/save-datapoint
                         (merge
                          context
                          {:event event-name
                           :event-version 0
                           :video-time (.currentTime player)})])))))

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Ended
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-ended
    {:description
     (str "HTML5 Video 'ended' event. \"Playback has stopped because the end "
          "of the media resource was reached.\"")
     :changelog
     [{:date "13MAY18"
       :event-version 0
       :data video-event-context}]}))

(kyber-event-fx
 :media-player/ended
 (fn [{:keys [db]} [event-name {:keys [player-key context]}]]
   (let [player (get-in db [:media-player player-key :player])]
     (cond-> {:db db}
       (some? player)
       (assoc :dispatch-n (cond-> [[:analytics/save-datapoint
                                    (merge
                                     context
                                     {:event event-name
                                      :event-version 0
                                      :video-time (.currentTime player)})]]
                            (= player-key :main-activity)
                            (conj [:activity/end-activity])))))))

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Error
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-error
    {:description
     (str "HTML5 Video 'error' event. \"An error occurs while fetching the "
          "media data or the type of the resource is not supported media "
          "format.\"")
     :changelog
     [{:date "13MAY18"
       :event-version 0
       :data video-event-context}]}))

(kyber-event-fx
 :media-player/error
 (fn [{:keys [db]} [event-name {:keys [player-key context]}]]
   (let [player (get-in db [:media-player player-key :player])]
     (cond-> {:db db}
       (some? player)
       (assoc :dispatch [:analytics/save-datapoint
                         (merge
                          context
                          {:event event-name
                           :event-version 0
                           :video-time (.currentTime player)})])))))

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Loaded Data
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-loadeddata
    {:description
     (str "HTML5 Video 'loadeddata' event. \"The user agent can render the "
          "media data at the current playback position for the first time.\"")
     :data-warehouse true
     :changelog
     [{:date "13MAY18"
       :event-version 0
       :data video-event-context}]}))

(kyber-event-fx
 :media-player/loadeddata
 (fn [{:keys [db]} [event-name {:keys [player-key context]}]]
   (let [player (get-in db [:media-player player-key :player])]
     (cond-> {:db db}
       (some? player)
       (assoc :dispatch [:analytics/save-datapoint
                         (merge
                          context
                          {:event event-name
                           :event-version 0
                           :video-time (.currentTime player)})])))))

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Loaded Metadata
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-loadedmetadata
    {:description
     (str "HTML5 Video 'loadedmetadata' event. \"The user agent has just "
          "determined the duration and dimensions of the media resource and "
          "the text tracks are ready.\"")
     :data-warehouse true
     :changelog
     [{:date "13MAY18"
       :event-version 0
       :data video-event-context}]}))

(kyber-event-fx
 :media-player/loadedmetadata
 (fn [{:keys [db]} [event-name {:keys [player-key context]}]]
   (let [{:keys [player options]} (get-in db [:media-player player-key])
         {:keys [segs]} options]
     (if-not (some? player)
       {:db db}
       (cond-> {:db
                (-> db
                    (player-assoc-in player-key :duration (.duration player))
                    (player-assoc-in player-key :loaded-metadata true)
                    (player-assoc-in player-key :suspended false))
                :dispatch-n [[:analytics/save-datapoint
                              (merge
                               context
                               {:event event-name
                                :event-version 0
                                :video-time (.currentTime player)})]
                             [:media-player/set-duration {:player player
                                                          :player-key player-key
                                                          :context context}]]}
         (and (some? segs)
              (= player-key :main-activity))
         (assoc :media-player/seek {:player player
                                    :position segs}))))))

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Loadstart
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-loadstart
    {:description
     (str "HTML5 Video 'loadstart' event. \"The user agent begins looking for "
          "media data, as part of the resource selection algorithm.\"")
     :data-warehouse true
     :changelog
     [{:date "13MAY18"
       :event-version 0
       :data video-event-context}]}))

(kyber-event-fx
 :media-player/loadstart
 (fn [{:keys [db]} [event-name {:keys [player-key context]}]]
   (let [player (get-in db [:media-player player-key :player])]
     (cond-> {:db db}
       (some? player)
       (assoc :dispatch [:analytics/save-datapoint
                         (merge
                          context
                          {:event event-name
                           :event-version 0
                           :video-time (.currentTime player)})])))))

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Pause
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-pause
    {:description
     (str "HTML5 Video 'pause' event. \"The element has been paused. Fired "
          "after the pause() method has returned.\"")
     :changelog
     [{:date "13MAY18"
       :event-version 0
       :data video-event-context}]}))

(kyber-event-fx
 :media-player/pause
 (fn [{:keys [db]} [event-name {:keys [player-key context]}]]
   (let [player (get-in db [:media-player player-key :player])]
     (if (some? player)
       {:db (-> db
                (player-assoc-in player-key :player-state :pause)
                (player-assoc-in player-key :buffering false))
        :dispatch [:analytics/save-datapoint
                   (merge
                    context
                    {:event event-name
                     :event-version 0
                     :video-time (.currentTime player)})]}
       {:db db}))))

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Play
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-play
    {:description
     (str "HTML5 Video 'play' event. \"The element is no longer paused. Fired "
          "after the play() method has returned, or when the autoplay "
          "attribute has caused playback to begin.\"")
     :changelog
     [{:date "13MAY18"
       :event-version 0
       :data video-event-context}]}))

(kyber-event-fx
 :media-player/play
 (fn [{:keys [db]} [event-name {:keys [player-key context]}]]
   (let [player (get-in db [:media-player player-key :player])]
     (if (some? player)
       {:db (-> db
                (player-assoc-in player-key :player-state :play)
                (player-assoc-in player-key :buffering false)
                (player-assoc-in player-key :volume (.volume player))
                (player-assoc-in player-key :rate (.playbackRate player)))
        :dispatch [:analytics/save-datapoint
                   (merge
                    context
                    {:event event-name
                     :event-version 0
                     :video-time (.currentTime player)})]}
       {:db db}))))

(kyber-event-db
 :media-player/play-on-initialize
 (fn [db [_ {:keys [player-key]}]]
   (assoc-in db [:media-player player-key :play-on-initialize] true)))

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Playing
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-playing
    {:description
     (str "HTML5 Video 'playing' event. \"Playback is ready to start after "
          "having been paused or delayed due to lack of media data.\"")
     :data-warehouse true
     :changelog
     [{:date "13MAY18"
       :event-version 0
       :data video-event-context}]}))

(kyber-event-fx
 :media-player/playing
 (fn [{:keys [db]} [event-name {:keys [player-key context]}]]
   (let [player (get-in db [:media-player player-key :player])]
     (if (some? player)
       {:db (player-assoc-in db player-key :buffering false)
        :dispatch [:analytics/save-datapoint
                   (merge
                    context
                    {:event event-name
                     :event-version 0
                     :video-time (.currentTime player)})]}
       {:db db}))))

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Progress
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-progress
    {:description
     "HTML5 Video 'progress' event. \"The user agent is fetching media data.\""
     :data-warehouse true
     :changelog
     [{:date "13MAY18"
       :event-version 0
       :data video-event-context}]}))

(kyber-event-fx
 :media-player/progress
 (fn [{:keys [db]} [event-name {:keys [player-key context]}]]
   (let [player (get-in db [:media-player player-key :player])]
     (cond-> {:db db}
       (some? player)
       (assoc :dispatch [:analytics/save-datapoint
                         (merge
                          context
                          {:event event-name
                           :event-version 0
                           :video-time (.currentTime player)})])))))

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Rate Change
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-ratechange
    {:description
     (str "HTML5 Video 'ratechange' event. \"Either the defaultPlaybackRate "
          "or the playbackRate attribute has just been updated.\"")
     :changelog
     [{:date "13MAY18"
       :event-version 0
       :data (merge video-event-context
                    {:rate :number})}]}))

(kyber-event-fx
 :media-player/ratechange
 (fn [{:keys [db]} [event-name {:keys [player-key context]}]]
   (let [player (get-in db [:media-player player-key :player])]
     (if (some? player)
       (let [rate (.playbackRate player)]
         {:db (player-assoc-in db player-key :rate rate)
          :dispatch [:analytics/save-datapoint
                     (merge
                      context
                      {:event event-name
                       :event-version 0
                       :video-time (.currentTime player)
                       :rate rate})]})
       {:db db}))))

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Resize
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-resize
    {:description
     (str "HTML5 Video 'resize' event. \"One or both of the videoWidth and "
          "videoHeight attributes have just been updated.\"")
     :data-warehouse true
     :changelog
     [{:date "13MAY18"
       :event-version 0
       :data video-event-context}]}))

(kyber-event-fx
 :media-player/resize
 (fn [{:keys [db]} [event-name {:keys [player-key context]}]]
   (let [player (get-in db [:media-player player-key :player])]
     (cond-> {:db db}
       (some? player)
       (assoc :dispatch [:analytics/save-datapoint
                         (merge
                          context
                          {:event event-name
                           :event-version 0
                           :video-time (.currentTime player)})])))))

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Seeked
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-seeked
    {:description
     (str "HTML5 Video 'seeked' event. \"The seeking IDL attribute changed to "
          "false after the current playback position was changed.\"")
     :changelog
     [{:date "13MAY18"
       :event-version 0
       :data video-event-context}]}))

(kyber-event-fx
 :media-player/seeked
 (fn [{:keys [db]} [event-name {:keys [player-key context]}]]
   (let [player (get-in db [:media-player player-key :player])]
     (cond-> {:db db}
       (some? player)
       (assoc :dispatch [:analytics/save-datapoint
                         (merge
                          context
                          {:event event-name
                           :event-version 0
                           :video-time (.currentTime player)})])))))

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Seeking
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-seeking
    {:description
     (str "HTML5 Video 'seeking' event. \"The seeking IDL attribute changed "
          "to true, and the user agent has started seeking to a new "
          "position.\"")
     :changelog
     [{:date "13MAY18"
       :event-version 0
       :data video-event-context}]}))

(kyber-event-fx
 :media-player/seeking
 (fn [{:keys [db]} [event-name {:keys [player-key context]}]]
   (let [player (get-in db [:media-player player-key :player])
         {:keys [pre-seek-time post-seek-time]} (j/lookup (j/get player :state))]
     (cond-> {:db db}
       (some? player)
       (assoc :dispatch [:analytics/save-datapoint
                         (merge
                          context
                          {:event event-name
                           :event-version 0
                           :video-time (.currentTime player)
                           :previous-time pre-seek-time
                           :current-time post-seek-time})])))))

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Stalled
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-stalled
    {:description
     (str "HTML5 Video 'stalled' event. \"The user agent is trying to fetch "
          "media data, but data is unexpectedly not forthcoming.\"")
     :data-warehouse true
     :changelog
     [{:date "13MAY18"
       :event-version 0
       :data video-event-context}]}))

(kyber-event-fx
 :media-player/stalled
 (fn [{:keys [db]} [event-name {:keys [player-key context]}]]
   (let [player (get-in db [:media-player player-key :player])]
     (cond-> {:db db}
       (some? player)
       (assoc :dispatch [:analytics/save-datapoint
                         (merge
                          context
                          {:event event-name
                           :event-version 0
                           :video-time (.currentTime player)})])))))

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Suspend
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-suspend
    {:description
     (str "HTML5 Video 'suspend' event. \"The user agent is intentionally "
          "not currently fetching media data.\"")
     :changelog
     [{:date "13MAY18"
       :event-version 0
       :data video-event-context}]}))

(kyber-event-fx
 :media-player/suspend
 (fn [{:keys [db]} [event-name {:keys [player-key context]}]]
   (let [player (get-in db [:media-player player-key :player])]
     (cond-> {:db (player-assoc-in db player-key :suspended true)}
       (some? player)
       (assoc :dispatch [:analytics/save-datapoint
                         (merge
                          context
                          {:event event-name
                           :event-version 0
                           :video-time (.currentTime player)})])))))

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Time Update
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-timeupdate
    {:description
     (str "HTML5 Video 'timeupdate' event. \"The current playback position "
          "changed as part of normal playback or in an especially interesting "
          "way, for example discontinuously.\"")
     :data-warehouse true
     :changelog
     [{:date "13MAY18"
       :event-version 0
       :data video-event-context}]}))

(defn- active-time?
  [position time]
  (and (<= position (+ time active-moment-threshold))
       (>= position time)))

(defn- trigger-moment?
  [{:keys [display-at]} position]
  (active-time? position display-at))

(defn get-triggered-resource
  [{:keys [is-main-activity
           seen-moments
           splash-is-active
           resources
           resource-uuid
           time]}]

  (when (and
         ;; Moments can only be triggered when watching the PLO
         (true? is-main-activity)

         ;; If a moment occurs at 00:00:00 then the moment is
         ;; displayed before the video even starts since the
         ;; timeupdate function is triggered on initial resource load.
         (false? splash-is-active)

         ;; Only search when there is not already a triggered resource
         ;; on screen
         (not (some? resource-uuid)))
    (let [resource
          (->> resources
               (filter #(trigger-moment? % time))
               (first))

          last-seen-at (when (some? resource)
                         (-> seen-moments
                             (get (:uuid resource))
                             (last)))

          ;; If the moment has been previously triggered, make sure that
          ;; the moment was not just triggered causing a double trigger
          can-re-trigger (if (some? last-seen-at)
                           (t/before? (t/plus last-seen-at (t/seconds 5))
                                      (t/now))
                           true)]

      (when (and (some? resource)
                 (true? can-re-trigger))
        resource))))

(defn timeupdate
  [{:keys [db]} [event-name {:keys [player-key context]}]]
  (let [{:keys [player options]}
        (get-in db [:media-player player-key])]
    (if-not (some? player)
      {:db db}
      (let [{:keys [activity-uuid curriculum-uuid]}
            (get-in db [:router :route-params])

            moment-uuid
            (get-in db [:router :route-query "segm"])

            {:keys [sege]}
            options

            time
            (.currentTime player)

            end-of-segment
            (and (some? sege)
                 (>= time sege))

            is-main-activity
            (= player-key :main-activity)

            {:keys [user-notes moments
                    completion-threshold]}
            (:activity db)

            {shout-moments "shout"
             whisper-moments "whisper"}
            (group-by :behavior moments)

            designed-moments
            (when is-main-activity
              (map-user-notes-to-moments
               {:user-notes user-notes
                :moments shout-moments}))

            whispers
            (when is-main-activity
              (map-user-notes-to-moments
               {:user-notes user-notes
                :moments whisper-moments}))

            active-moment-uuid
            (get-in db [:activity :designed-moment-active])

            active-whisper-uuid
            (get-in db [:activity :whisper-active])

            triggered-moment
            (get-triggered-resource
             {:is-main-activity is-main-activity
              :splash-is-active (get-in db [:activity :show-activity-splash])
              :resource-uuid active-moment-uuid
              :resources designed-moments
              :time time
              :seen-moments (get-in db [:activity :seen-moments])})

            triggered-whisper
            (get-triggered-resource
             {:is-main-activity is-main-activity
              :splash-is-active (get-in db [:activity :show-activity-splash])
              :resource-uuid active-whisper-uuid
              :resources whispers
              :time time
              :seen-moments (get-in db [:activity :seen-moments])})

            hide-whisper
            (when (some? active-whisper-uuid)
              (let [hide-at (->> (get-in db [:activity :moments])
                                 (filter #(= (:uuid %) active-whisper-uuid))
                                 (first)
                                 :hide-at)]
                (active-time? hide-at time)))]
        (cond-> {:db (-> db
                         (player-assoc-in player-key :time time)
                         (player-assoc-in player-key :buffering false))
                 :dispatch-n [[:analytics/save-datapoint
                               (merge
                                context
                                {:event event-name
                                 :event-version 0
                                 :video-time time})]]}

          is-main-activity
          (update :dispatch-n
                  conj
                  [:activity/save-activity-progress-data-point
                   {:event event-name
                    :video-time time}])

          is-main-activity
          (update-in [:db :activity :furthest-viewed-time]
                     #(if (> time %) time %))

          (and is-main-activity
               (> time completion-threshold))
          (assoc-in [:db :activity :has-crossed-percent-complete-threshold]
                    true)

          (some? triggered-moment)
          (assoc-in [:db :activity :designed-moment-active]
                    (:uuid triggered-moment))

          (some? triggered-whisper)
          (assoc-in [:db :activity :whisper-active]
                    (:uuid triggered-whisper))

          (true? hide-whisper)
          (update :dispatch-n
                  conj
                  [:activity/clear-active-whisper active-whisper-uuid])

          (or (some? triggered-moment)
              (some? triggered-whisper))
          (update :dispatch-n
                  conj
                  [:activity/save-activity-progress-data-point
                   {:event :activity/viewed-moment
                    :context-location :active-moment
                    :moment-uuid (if (some? triggered-moment)
                                   (:uuid triggered-moment)
                                   (:uuid triggered-whisper))}])

          (and is-main-activity
               (some? triggered-moment))
          (assoc :media-player/pause player)

          end-of-segment
          (assoc :media-player/pause player)

          end-of-segment
          (update-in [:db :media-player player-key :options]
                     #(dissoc % :sege :segs))

          (and is-main-activity
               end-of-segment)
          (update :dispatch-n
                  conj
                  [:notifier/create-alert
                   {:title "Back to Activity Review"
                    :message (str "You have finished watching "
                                  "the segment, would you like "
                                  "to take a note?")
                    :confirm-action (fn []
                                      (re-frame/dispatch
                                       [:notifier/clear-active-alert])

                                      (re-frame/dispatch
                                       [:activity/show-learning-stream
                                        {:moment {:uuid moment-uuid}
                                         :edit true}]))
                    :confirm-text "Take note"
                    :deny-text "Activity Review"
                    :deny-action
                    #(re-frame/dispatch
                      [:activity/update-activity-progress
                       {:activity-uuid activity-uuid
                        :curriculum-uuid curriculum-uuid
                        :dispatch-on-update
                        [:activity/update-activity-progress-re-route
                         {:activity-uuid activity-uuid
                          :curriculum-uuid curriculum-uuid
                          :route-params {:curriculum-uuid curriculum-uuid
                                         :activity-uuid activity-uuid
                                         :end-segment "results"}}]}])}]))))))

(kyber-event-fx :media-player/timeupdate timeupdate)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Volume Change
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-volumechange
    {:description
     (str "HTML5 Video 'volumechange' event. \"Either the volume attribute or "
          "the muted attribute has changed. Fired after the relevant "
          "attribute’s setter has returned.\"")
     :changelog
     [{:date "13MAY18"
       :event-version 0
       :data (merge video-event-context
                    {:volume :number})}]}))

(kyber-event-fx
 :media-player/volumechange
 (fn [{:keys [db]} [event-name {:keys [player-key context]}]]
   (let [player (get-in db [:media-player player-key :player])]
     (if (some? player)
       (let [volume (.volume player)]
         {:db (player-assoc-in db player-key :volume volume)
          :dispatch [:analytics/save-datapoint
                     (merge
                      context
                      {:event event-name
                       :event-version 0
                       :video-time (.currentTime player)
                       :volume volume})]})
       {:db db}))))

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Waiting
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-waiting
    {:description
     (str "HTML5 Video 'waiting' event. \"Playback has stopped because the "
          "next frame is not available, but the user agent expects that frame "
          "to become available in due course.\"")
     :data-warehouse true
     :changelog
     [{:date "13MAY18"
       :event-version 0
       :data video-event-context}]}))

(kyber-event-fx
 :media-player/waiting
 (fn [{:keys [db]} [event-name {:keys [player-key context]}]]
   (let [player (get-in db [:media-player player-key :player])]
     (if (some? player)
       {:db (player-assoc-in db player-key :buffering true)
        :dispatch [:analytics/save-datapoint
                   (merge
                    context
                    {:event event-name
                     :event-version 0
                     :video-time (.currentTime player)})]}
       {:db db}))))

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Player Side-effects
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(re-frame/reg-fx
 :media-player/play
 (fn [player]
   (when (some? player)
     (let [_time (.currentTime player)]

       ;; Rewind 1 second to regain context
       ;; NOTE This is causing a lot of problems with the player
       ;;      getting stuck. Removing until we require this
       ;;      feature
       #_(when-not (zero? time)
           (.currentTime player (- time playback-buffer-time)))

       (.play player)))))

(re-frame/reg-fx
 :media-player/teardown
 (fn [^js player]
   (when player
     ;; Remove all listeners
     (doseq [event-name html5-media-player-events]
       (.off player (name event-name)))

     ;; videojs cleanup
     (.dispose player))))

(re-frame/reg-fx
 :media-player/pause
 (fn [player]
   (when player
     (.pause player))))

(re-frame/reg-fx
 :media-player/seek
 ;; https://github.com/videojs/video.js/issues/1324
 (fn [{:keys [^js player position]}]
   (when (some? player)
     (let [interval-id (atom nil)]
       (reset! interval-id
               (.setInterval
                js/window
                (fn []
                  (let [seekable (.seekable player)
                        is-seekable (and
                                     (some? seekable)
                                     (-> seekable .-length pos?))
                        ready-state (.readyState player)
                        has-duration (pos? (.duration player))]
                    (when (and is-seekable
                               has-duration
                               (= 4 ready-state))
                      ;; This caches the current time and seek time in the player object so that it can be
                      ;; read in the seeking event
                      (j/call player :setState #js {:pre-seek-time (.currentTime player)
                                                    :post-seek-time position})
                      (.clearInterval js/window @interval-id)
                      (.currentTime player position))))
                10))))))

(re-frame/reg-fx
 :media-player/volume
 (fn [{:keys [^js player volume]}]
   (when (and player volume)
     (.volume player volume))))

(re-frame/reg-fx
 :media-player/playback-rate
 (fn [{:keys [^js player rate]}]
   (when (and player rate)
     (.playbackRate player rate))))
